import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import ILink from '../../../types/ILink';
import { LazyView, StaticView } from '../../../types/BlockWithViews';
import { asFetchableBlock, useBlockContext } from '../../../_lib/BlockFetcher';
import { BlockType } from '../../../types/api/ElementType';
import MemberDescription from './partials/MemberDescription';
import { TeamroomManagementButtons } from './partials/TeamroomManagementButtons';
import { TeamroomManagementActiveView } from './partials/TeamroomManagementActiveView';
import { TeamroomView } from './TeamroomManagement.types';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { BlockBarBase } from '../../../_lib/components/BlockBar';

type EmbeddedWithForm = {
  form?: Record<string, unknown>;
};

type EmbeddedWithProfileLinkAndForm = {
  profileLinkActivationRequester: ILink;
  form?: Record<string, unknown>;
};

type TeamroomManagementViews = {
  requestActivation?: StaticView<EmbeddedWithForm>;
  acceptActivation?: StaticView<EmbeddedWithProfileLinkAndForm>;
  rejectActivation?: StaticView<EmbeddedWithForm>;
  invite?: LazyView;
  inviteUnregistered?: LazyView;
  leave?: LazyView;
  join?: LazyView;
  joinRequest?: LazyView;
};

type TeamroomManagementProps = {
  profileLink: ILink;
  roles?: string[] | null;
  teamroomMembershipType?: string | null;
  views?: TeamroomManagementViews | null;
  profileLinkActivationRequester?: ILink | null;
};

export function TeamroomManagement({
  profileLink,
  profileLinkActivationRequester,
  roles,
  teamroomMembershipType,
  views,
}: TeamroomManagementProps) {
  const { uuid, elementType } = useBlockContext();
  const blockId = `${elementType}-${uuid}`;
  const { t } = useSonTranslation('TeamroomManagement');

  const [searchParams] = useSearchParams();
  const [currentActiveView, setCurrentActiveView] = useState<TeamroomView | null>(() => {
    let activeView = searchParams.get('active-view') as TeamroomView;

    if (typeof activeView === 'string') {
      activeView = activeView.split('.').at(1) as TeamroomView;
    }

    return activeView;
  });

  const toggleView = useCallback((viewKey: TeamroomView) => {
    setCurrentActiveView((current) => {
      if (current === viewKey) {
        return null;
      }
      return viewKey;
    });
  }, []);

  const closeView = useCallback(() => {
    setCurrentActiveView(null);
  }, []);

  return (
    <>
      <BlockBarBase heading={t('headline')} invisibleHeading modifier="border-bottom" blockId={blockId}>
        <div className="teamroom-management__blockbar-container">
          <div className="teamroom-management__member-description">
            <MemberDescription
              roles={roles ?? []}
              membershipType={teamroomMembershipType ?? ''}
              profileLink={profileLink}
              profileLinkActivationRequester={profileLinkActivationRequester}
            />
          </div>
          <TeamroomManagementButtons
            views={views as Record<TeamroomView, LazyView> | null | undefined}
            currentActiveView={currentActiveView}
            toggleView={toggleView}
            blockId={blockId}
          />
        </div>
      </BlockBarBase>
      <TeamroomManagementActiveView
        currentActiveView={currentActiveView}
        views={views as Record<string, unknown>}
        closeView={closeView}
      />
    </>
  );
}

export const TeamroomManagementBlock = asFetchableBlock(TeamroomManagement, BlockType.teamroommanagement);
export type TeamroomManagementBlock = React.ComponentProps<typeof TeamroomManagementBlock>;
